import { isViewNavRoute } from './CanvasViewNav.js'

// THE SIDEBAR CARD — one glass for every sidebar card and every side panel
// (Nika 2026-10-30, pick "A: navbar glass"): the brand `navbar` surface (the
// header row's glass: an 85% / 95% fill under a 35px blur, max-contrast ink),
// the `float` lift at the component (a theme cannot name a shadow primitive),
// and the CommonCard geometry (radiusCard, padding A). Every AppSidebar child
// takes it through `childExtends` below; the canvas side panels extend it; a
// card sets none of these keys itself (__tests__/sidebarGlass.test.js).
// Not `card`: that theme's blur exists only as the ambient skin's variable,
// and a theme's backdropFilter is emitted literally (`none`), so the rails
// painted a translucent fill with no blur behind it (measured on dev
// 2026-10-30, /studio SECTIONS). No fontSize: typography tokens are em-based,
// and the column already sets `Z`.
// A backdrop-filter makes the card the containing block of a fixed
// descendant, so no part of a card is position: fixed (the same test).
// Reduced transparency: the opaque `dialog` sheet, no blur (DESIGN_SPEC 6.5).
export const AppSidebarCard = {
  extends: 'CommonCard',
  theme: 'navbar',
  shadow: 'float',
  '@reduceTransparency': { theme: 'dialog', backdropFilter: 'none' }
}

export const AppSidebar = {
  // THE PAGE-NAV MARK (DESIGN-WL-PAGESLOT-OPEN-SPLIT-SEARCH-1): every page's
  // own nav is an AppSidebar, and says so. The PageSlot window's sidebar
  // icon finds the page's nav by it (canvas components/PageSlot.js
  // `_toggleSidebar`) and hides / shows it through the same per-page choice
  // as the sidebar's own button below (functions/pageSidebar.js). `attr`
  // merges across extends, so every rail that extends this one carries it.
  attr: { 'data-app-sidebar': '' },

  // THE SIDE AND THE CHOICE (Nika 2026-10-29 — functions/pageSidebar.js):
  // the sidebar sits on the side the navbar does not take (right beside a
  // left rail, left everywhere else) and the person can hide it, per page.
  // Two attributes carry the posture; the rules below are static, so a
  // change costs one style recalc. Reactive through what pageSidebarRead
  // reads (the look, the phone flag, the address, the pane keys).
  //
  // THE OPT-IN: a page moves its sidebar onto the side rule and gets the hide
  // button by setting `pageSidebar: true` on its instance — one page at a
  // time, each checked on both sides. Without it the sidebar stays on the
  // left with no button (no `data-sidebar-side`), as before; the hidden
  // choice and the reopen tab hold for every page, because the PageSlot
  // window's icon reaches every page's sidebar.
  pageSidebar: false,
  subscribeTo: [
    '_homeLook',
    'userPrefs',
    '_isPhoneViewport',
    'currentPath',
    '_canvasSlot',
    '_canvasSlotSecond'
  ],
  'data-sidebar-side': (el, s) => {
    const now = el.call('pageSidebarRead', s.root, el) || {}
    return now.follows ? now.side || 'left' : null
  },
  'data-sidebar-hidden': (el, s) =>
    (el.call('pageSidebarRead', s.root, el) || {}).hidden ? 'true' : null,
  // THE BUTTON'S CORNER: the column says when its hide button shows (opted in,
  // on a host that remembers, not a phone), so the first card can make room
  // for it — the two rules after the mirror below.
  'data-sidebar-toggleable': (el, s) => {
    const now = el.call('pageSidebarRead', s.root, el) || {}
    return now.follows && now.toggleable ? 'true' : null
  },
  // THE MIRROR: after the page (`order`), the padding turned round — the
  // X2 edge gap on the screen's side, Z towards the page. The page sheet
  // (AppMain) keeps its X2 gutter on its right; the sidebar steps back over
  // it (`marginLeft: -X2`), so the gap between the sheet and the first card
  // is Z on both sides, as before. A narrow pane keeps the stacked posture
  // (the sidebar above the page).
  '&[data-sidebar-side="right"]': {
    order: '1',
    padding: 'X2 X2 X2 Z',
    marginLeft: '-X2',
    '@mobileL': { order: '0', padding: 'Z A', marginLeft: '0' },
    '@appMobile': { order: '0', padding: 'Z A', marginLeft: '0' }
  },
  // THE FREE CORNER: the hide button sits in the first card's top-right corner
  // on every page. A first card whose first row is a caption (VIEWS, a group
  // heading, an eyebrow) leaves that corner free by itself. A card that starts
  // with a full-width row (a link list, a selectable row) marks itself
  // `data-sidebar-band`: its top padding becomes a head band — the button with
  // one Y above and below it — so the button never sits on a link. A header row
  // whose own actions end at that corner (the calendar's month arrows) marks
  // itself `data-sidebar-head-end` and keeps them clear of the button (the
  // band, less the card's own A padding). Both only while the button shows.
  '&[data-sidebar-toggleable] > [data-sidebar-band]': {
    paddingTop: 'calc(var(--size-controlHCompact) + 2 * var(--spacing-Y))',
    '@coarse': { paddingTop: 'calc(var(--size-controlH) + 2 * var(--spacing-Y))' }
  },
  '&[data-sidebar-toggleable] [data-sidebar-head-end]': {
    paddingRight: 'calc(var(--size-controlHCompact) + 2 * var(--spacing-Y) - var(--spacing-A))',
    '@coarse': {
      paddingRight: 'calc(var(--size-controlH) + 2 * var(--spacing-Y) - var(--spacing-A))'
    }
  },
  // HIDDEN: the column keeps its edge gaps and shrinks to the reopen tab; every
  // card leaves (display none — out of the flow and out of the Tab order).
  // The width floors a page sets on its own instance are released here (an
  // attribute rule outranks an instance's single class). A rail that IS its
  // card (the column extends AppSidebarCard, its Middle unwrapped) paints
  // nothing while hidden, so the tab stands alone and no glass box stays.
  '&[data-sidebar-hidden]': {
    width: 'auto',
    minWidth: '0',
    maxWidth: 'none',
    pointerEvents: 'none',
    background: 'transparent',
    boxShadow: 'none',
    backdropFilter: 'none'
  },
  '&[data-sidebar-hidden] > *': { display: 'none' },
  '&[data-sidebar-hidden] > [data-sidebar-reopen]': { display: 'flex', pointerEvents: 'auto' },

  flow: 'y',
  width: 'G',
  minWidth: 'G',
  maxWidth: '100%',
  boxSizing: 'border-box',
  alignSelf: 'stretch',
  // The positioning context of the hide button below (a page that floats its
  // sidebar sets its own `absolute` / `fixed`).
  position: 'relative',
  color: 'title',
  // visible, NOT hidden: this wrapper is invisible layout chrome while the
  // CARDS inside it (childExtends AppSidebarCard) carry the
  // `0 8px 32px` float shadow — a hidden overflow guillotined every card's
  // shadow at the nav box on all four sides, so the rails read as flat
  // cutouts (2026-07-29 per Nika "fix that shadows cut on canvas
  // sidebars"). Scrolling lives INSIDE the cards (each rail's Middle has
  // its own overflow: auto), so the wrapper never needed to clip.
  overflow: 'visible',
  // One step (X2) on the top, outer and bottom edges, so a rail card floats
  // evenly inside the rounded page sheet; Z is the gap to the page beside
  // it. The top used to be '-': the card met the sheet's top edge while it
  // stood 5px in on the left and the bottom (measured 2026-10-24 on every
  // comm-host rail and on the workspace /chat).
  padding: 'X2 Z X2 X2',
  gap: 'Z',
  fontSize: 'Z',
  flex: '0 0 auto',

  '@mobileL': {
    width: '100%',
    minWidth: '0',
    maxWidth: '100%',
    maxHeight: '42dvh',
    padding: 'Z A',
    overflow: 'auto'
  },
  // The PANE twin is not a copy of the phone rule (design-direction.md §4):
  // `dvh` is the WINDOW's height, and a pane is shorter than its window. At
  // 42dvh the stacked rail took 381px of a 798px pane and left the task 363px
  // (45%), measured live 2026-09-22 on /people and /bookmarks at 715px. 30dvh —
  // the cap the files rail already uses — gives the rail 272px and the task
  // 471px (59%), measured the same way; the rail scrolls inside itself, as before (§5: navigation must
  // leave room for the task).
  '@appMobile': {
    width: '100%',
    minWidth: '0',
    maxWidth: '100%',
    maxHeight: '30dvh',
    padding: 'Z A',
    overflow: 'auto'
  },

  childExtends: 'AppSidebarCard',

  childProps: {
    flow: 'y',
    minWidth: '0',
    maxWidth: '100%'
  },

  // Global view switcher — its OWN card (AppSidebarCard via childExtends) above
  // the other sidebar cards, jumping between Frontend (/studio), Infra (/infra),
  // Tasks (/tickets), Agents (/agents). A base key, so it renders above the
  // extenders' Middle. (2026-06-18 per Nika "move the canvas switcher to
  // another card inside the sidebar above Middle".) SELF-GATING: visible ONLY
  // on the routes the switcher itself contains (isViewNavRoute) — any other
  // rail extending AppSidebar (calendar, resolutions, feed, preview, …)
  // hides it automatically, so per-rail `ViewSwitcher: null` overrides are an
  // eager opt-out, not load-bearing. (2026-07-24 per Nika "ViewSwitcher in
  // sidebar should be only visible for pages that it contains".)
  ViewSwitcher: {
    hide: (el, s) => !isViewNavRoute(s, el),
    subscribeTo: ['currentPath', '_canvasSlot', '_canvasSlotSecond'],
    flexShrink: '0',
    ViewNav: { extends: 'CanvasViewNavGroup' }
  },

  // THE HIDE BUTTON — one place on every page: the top-RIGHT corner of the
  // sidebar's first card, on either side (card headers start at the left, so
  // that corner is free; the tasks and studio x sat there), the house icon
  // button (PageSlotHeaderBtn: 36px circle, `iconSm` glyph, hover / pressed
  // washes, the plugin tooltip). The glyph is a window with its column on
  // the sidebar's own side. Not a card: `ignoreChildExtends` keeps the
  // column's AppSidebarCard off it. Only on a sidebar that opted in
  // (`pageSidebar: true` — its first card's own corner is checked free);
  // absent where the host cannot remember the choice, and on a phone
  // (MobileRailSheet is the phone rail).
  SidebarToggle: {
    extends: 'PageSlotHeaderBtn',
    ignoreChildExtends: true,
    subscribeTo: ['_homeLook', 'userPrefs', '_isPhoneViewport', 'currentPath'],
    hide: (el, s) => {
      const now = el.call('pageSidebarRead', s.root, el) || {}
      return !now.toggleable || !now.follows
    },
    'data-sidebar-toggle': 'true',
    'data-side': (el, s) => (el.call('pageSidebarRead', s.root, el) || {}).side || 'left',
    position: 'absolute',
    zIndex: '2',
    top: 'calc(var(--spacing-X2) + var(--spacing-Y))',
    right: 'calc(var(--spacing-Z) + var(--spacing-Y))',
    // the column's right padding is X2 on the right side (the mirror below)
    '&[data-side="right"]': { right: 'calc(var(--spacing-X2) + var(--spacing-Y))' },
    ':active': { background: 'selected', color: 'title' },
    title: (el) => el.call('polyglot', 'canvas.pageSlot.sidebarHide'),
    // An icon-only control: its name is the label, not the tooltip alone.
    'aria-label': (el) => el.call('polyglot', 'canvas.pageSlot.sidebarHide'),
    'aria-expanded': 'true',
    Icon: {
      name: (el, s) =>
        (el.call('pageSidebarRead', s.root, el) || {}).side === 'right'
          ? 'lnSidebarRight'
          : 'lnSidebarLeft'
    },
    onClick: (e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      const now = el.call('pageSidebarRead', s.root, el) || {}
      if (!now.toggleable) return
      el.call('pageSidebarSet', now.key, true)
      // The button leaves with the cards: the focus goes to the tab that
      // brings them back (once the hidden rule has landed).
      const tab = el.parent && el.parent.SidebarReopen && el.parent.SidebarReopen.ReopenBtn
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => {
          if (tab && tab.node && typeof tab.node.focus === 'function') {
            tab.node.focus({ preventScroll: true })
          }
        })
      }
    }
  },

  // THE REOPEN TAB — at the page edge while the sidebar is hidden: the
  // collapsed column keeps its edge gaps, so the tab sits where the first
  // card's outer corner was. A small floating island (the sidebar card's own
  // glass and lift, as a pill) around the same icon button; the column's
  // hidden rule shows it, nothing else.
  // On every page sidebar, opted in or not: a choice made from a PageSlot
  // window reaches the page at its own URL, and this is the way back there.
  SidebarReopen: {
    extends: 'AppSidebarCard',
    ignoreChildExtends: true,
    'data-sidebar-reopen': 'true',
    display: 'none',
    flexShrink: '0',
    alignSelf: 'flex-start',
    round: 'radiusPill',
    padding: 'V',

    ReopenBtn: {
      extends: 'PageSlotHeaderBtn',
      subscribeTo: ['_homeLook', 'userPrefs', '_isPhoneViewport', 'currentPath'],
      color: 'title',
      ':active': { background: 'selected', color: 'title' },
      title: (el) => el.call('polyglot', 'canvas.pageSlot.sidebarShow'),
      'aria-label': (el) => el.call('polyglot', 'canvas.pageSlot.sidebarShow'),
      'aria-expanded': 'false',
      // The tip opens into the page, away from the edge the tab sits on.
      tooltipPlacement: (el, s) =>
        (el.call('pageSidebarRead', s.root, el) || {}).side === 'right' ? 'left' : 'right',
      Icon: {
        name: (el, s) =>
          (el.call('pageSidebarRead', s.root, el) || {}).side === 'right'
            ? 'lnSidebarRight'
            : 'lnSidebarLeft'
      },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        const now = el.call('pageSidebarRead', s.root, el) || {}
        if (!now.toggleable) return
        el.call('pageSidebarSet', now.key, false)
        const bar = el.parent && el.parent.parent
        const toggle = bar && bar.SidebarToggle
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => {
            if (toggle && toggle.node && typeof toggle.node.focus === 'function') {
              toggle.node.focus({ preventScroll: true })
            }
          })
        }
      }
    }
  }
}

// Sidebar people group — caption + list of `AppPersonRow`s. Used inside
// `PeopleSection`, same contract as governance calendar sidebar:
//   PeopleSection: {
//     TeamGroup: { extends: 'PeopleGroup', label: 'TEAM', groupName: 'team' }
//   }
//
// This is the ONLY definition of PeopleGroup/AppPersonRow in the monorepo
// (a duplicate copy in workspace/packages/workspace/components/AppSidebar.js
// was silently shadowing this one under the shell's project-barrel-wins
// precedence — see tickets/workspace.md, AppPersonRow/PeopleGroup
// duplicate-name collision). It lives HERE, not in the workspace project,
// because canvas's standalone production bundle links `shared` but not
// `workspace` (packages/canvas/sharedLibraries.js has no workspace import)
// and canvas/pages/dashboard.js does `extends: 'PeopleGroup'` — removing it
// from shared instead of workspace would have broken that build. This
// merges both former copies' behavior: the `|| []` null-safe guard, the
// `s.user?.*` wrapped-row fallbacks (handles both flat person rows and
// `{ user: {...} }`-wrapped rows), AND the onClick popover that only the
// workspace copy used to have — now `el.call('openPersonPopover', …)` on the
// shell-root dropdown layer instead of the retired `selectedPerson` card.
export const PeopleGroup = {
  flow: 'y',
  gap: 'Z',
  marginBottom: 'Z1',

  // The rail eyebrow — the same law as AppSidebarGroup's (brief §4.3 item 3):
  // `Z` in the rail's `Z` font context is the 11.8px `Y` role (em tokens).
  CaptionTitle: {
    fontSize: 'Z',
    fontWeight: '500',
    Text: {
      text: (el) => el.call('polyglot', el.parent?.parent?.label || '')
    }
  },

  List: {
    flow: 'y',
    gap: 'Y',
    childExtends: 'AppPersonRow',
    childrenAs: 'state',
    children: (el, s) => {
      const group = el.parent.groupName
      // Only 'team' group is active after equity/cap-table removal.
      // Advisor/investor/share-class groups returned empty in the old app
      // (peopleByShareClass filtered on equity data that no longer loads).
      if (group === 'team') return el.call('activeMembers') || []
      return []
    }
  }
}

export const AppPersonRow = {
  flow: 'x',
  align: 'center left',
  gap: 'Z',
  padding: 'Z Z1',
  margin: '- -Z1',
  round: 'radiusControl',
  fontSize: 'Z1',
  cursor: 'pointer',
  attr: {
    'data-personrow': 'true',
    'data-email': (el, s) => (s && (s.email || s.user?.email)) || ''
  },
  ':hover': { background: 'hover' },
  // Opens the shell-root person popover (PersonPopoverCard on the
  // @symbo.ls/dropdown layer): the row is the anchor, the layer owns
  // placement, viewport-edge auto-flip, outside-click AND Escape.
  //
  // It used to write `root.selectedPerson` with a hand-clamped x/y for a
  // page-mounted `position: fixed` card. Three defects lived in that: the
  // clamp asserted a 320px width the card never declared and was copy-pasted
  // in chat, the card had no Escape key, and inside a workspace pane it
  // opened far from the row or was clipped away entirely. What does that
  // inside a pane is the PANE's transform (its open motion and the phone
  // drag; a transform makes the containing block of a fixed descendant) and
  // its `overflow: hidden` — not its `container-type: inline-size`, which
  // makes no containing block for a fixed child (Chrome 154, WebKit 26.4,
  // measured 2026-10-05). The dropdown layer owns all three.
  onClick: (e, el, s) => {
    e.stopPropagation()
    const email = s.email || s.user?.email
    if (!email) return
    el.call('openPersonPopover', email, el, s.name || s.user?.name || '')
  },

  Avatar: {
    avatarType: 'initials',
    boxSize: 'B1 B1',
    src: (el, s) => el.call('avatarUrl', s && (s.name || s.user?.name))
  },

  Info: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',
    overflow: 'hidden',

    Name: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'title',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s?.name || s?.user?.name || s?.email || s?.user?.email || ''
    },
    Caption: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s?.position || s?.role || s?.membership?.role || ''
    }
  }
}
